/**
 * SDK-shipped prompt defaults, registered on the process-wide singleton.
 *
 * This module is the "one place" where every prompt the SDK ships is
 * enumerated. Hosts wanting to survey what's overridable can `console.log
 * (dddk.prompts.list())` and see everything registered here.
 *
 * IMPORTANT — this is the ONLY module allowed to call `registerDefault`
 * for a shipping prompt id. Any subsystem that wants to register a
 * new prompt should add it to `PROMPT_IDS` in `./registry.ts` and
 * register the default here, so nothing hides in module-init side-
 * effects deep inside a feature module.
 *
 * Ordering. Simple string constants first, function renderers last —
 * makes it obvious at a glance which prompts are static text vs.
 * assembled per-turn from context.
 */

import { PROMPT_IDS, promptRegistry } from './registry';
import { SYSTEM_PROMPT as INLINE_EDIT_SYSTEM } from '../agent/inline/parse';
import { MARKDOWN_EDIT_SYSTEM_PROMPT } from '../agent/plan/markdown';
import { TRANSLATE_SYSTEM_PROMPT } from '../modules/immersive-translate/prompt';
import { VOICE_CLEANUP_SYSTEM_PROMPT } from '../modules/voice/prompt';
import { DWELL_CLASSIFY_SYSTEM_PROMPT } from '../triggers/dwell/prompt';
import { buildPlanSystemPrompt, type PlanPromptContext } from '../agent/plan/prompt';
import { assembleSystemPrompt, type AssemblePromptInput } from '../agent/webagent/prompt';

let registered = false;

/**
 * Idempotent — safe to call from multiple entry points (the WebAgent
 * subsystem calls it lazily on first use so tree-shaking still works
 * for hosts that only touch the UI-primitive layer).
 */
export function registerBuiltinPrompts(): void {
  if (registered) return;
  registered = true;

  // ── Simple string prompts ──────────────────────────────────────
  promptRegistry.registerDefault(PROMPT_IDS.INLINE_EDIT, () => INLINE_EDIT_SYSTEM);
  promptRegistry.registerDefault(PROMPT_IDS.MARKDOWN_EDIT, () => MARKDOWN_EDIT_SYSTEM_PROMPT);
  promptRegistry.registerDefault(PROMPT_IDS.TRANSLATE, () => TRANSLATE_SYSTEM_PROMPT);
  promptRegistry.registerDefault(PROMPT_IDS.VOICE_CLEANUP, () => VOICE_CLEANUP_SYSTEM_PROMPT);
  promptRegistry.registerDefault(PROMPT_IDS.DWELL_CLASSIFY, () => DWELL_CLASSIFY_SYSTEM_PROMPT);

  // ── Context-driven prompts ─────────────────────────────────────
  // The Planner + WebAgent narrator receive rich per-run context
  // (sitemap, brand, persona) so we register the assembler functions
  // — hosts overriding these get the same context object.
  promptRegistry.registerDefault<PlanPromptContext>(
    PROMPT_IDS.PLANNER,
    (ctx) => buildPlanSystemPrompt(ctx ?? {}),
  );
  promptRegistry.registerDefault<AssemblePromptInput>(
    PROMPT_IDS.WEBAGENT_NARRATOR,
    (ctx) => assembleSystemPrompt(ctx ?? ({} as AssemblePromptInput)),
  );
  // CoT variant reuses the same assembler — the `cotMode` field in
  // ctx flips it internally. Registered as a separate id so hosts can
  // override just the thinking-mode narrator without touching the
  // regular one.
  promptRegistry.registerDefault<AssemblePromptInput>(
    PROMPT_IDS.WEBAGENT_COT,
    (ctx) => assembleSystemPrompt({ ...(ctx ?? {}), cotMode: true } as AssemblePromptInput),
  );
}
